import React, { useCallback, useEffect, useState } from "react";
import aiDevelopmentService from "../../services/aiDevelopmentService";

const RELATIONSHIP_TYPES = ["BLOCKS", "PARENT_OF", "RELATES_TO", "DUPLICATES", "PREDECESSOR_OF"];

// Shared by the story detail page and each task card - itemType/itemId identify which work item
// this panel is showing relationships *from the perspective of* (see AiWorkItemRelationshipService:
// displayType/otherType/otherId already account for which side of the edge this item is on, so the
// panel never needs to know or care whether a given row is stored as source or target).
export default function AiRelationshipsPanel({ itemType, itemId, t }) {
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ relationshipType: "BLOCKS", targetType: "TASK", targetId: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list =
        itemType === "STORY"
          ? await aiDevelopmentService.storyRelationships(itemId)
          : await aiDevelopmentService.taskRelationships(itemId);
      setRelationships(list || []);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [itemType, itemId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await aiDevelopmentService.createRelationship({
        sourceType: itemType,
        sourceId: itemId,
        relationshipType: form.relationshipType,
        targetType: form.targetType,
        targetId: form.targetId,
      });
      setForm({ ...form, targetId: "" });
      await load();
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    }
  };

  const remove = async (relationship) => {
    if (!window.confirm(t("aiDevelopment.relationships.deleteConfirm"))) return;
    setError("");
    try {
      await aiDevelopmentService.deleteRelationship(relationship.id, relationship.version);
      await load();
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    }
  };

  return (
    <div className="ai-relationships-panel">
      <h4>{t("aiDevelopment.relationships.title")}</h4>
      {error && <div className="ai-studio-alert">{error}</div>}
      {loading && !relationships.length ? (
        <p>{t("common.loading")}</p>
      ) : relationships.length === 0 ? (
        <p className="ai-repository-empty">{t("aiDevelopment.relationships.empty")}</p>
      ) : (
        <ul className="ai-relationship-list">
          {relationships.map((r) => (
            <li key={r.id} className="ai-relationship-item">
              <span className="ai-status">{t(`aiDevelopment.relationships.type.${r.displayType}`)}</span>
              <span>{t(`aiDevelopment.relationships.itemType.${r.otherType}`)} {r.otherId}</span>
              <button type="button" className="btn btn-default" onClick={() => remove(r)}>
                {t("common.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="ai-relationship-form" onSubmit={add}>
        <select
          value={form.relationshipType}
          onChange={(e) => setForm({ ...form, relationshipType: e.target.value })}
        >
          {RELATIONSHIP_TYPES.map((v) => (
            <option key={v} value={v}>
              {t(`aiDevelopment.relationships.type.${v}`)}
            </option>
          ))}
        </select>
        <select value={form.targetType} onChange={(e) => setForm({ ...form, targetType: e.target.value })}>
          <option value="STORY">{t("aiDevelopment.relationships.itemType.STORY")}</option>
          <option value="TASK">{t("aiDevelopment.relationships.itemType.TASK")}</option>
        </select>
        <input
          required
          placeholder={t("aiDevelopment.relationships.targetIdPlaceholder")}
          value={form.targetId}
          onChange={(e) => setForm({ ...form, targetId: e.target.value })}
        />
        <button className="btn btn-default" type="submit">
          {t("aiDevelopment.relationships.add")}
        </button>
      </form>
    </div>
  );
}
